import { Suspense, lazy, useEffect, useRef, type RefObject } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@revont/shared-ui';
import Lenis from 'lenis';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { updateRouteMetadata } from './utils/seo';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const ProductPage = lazy(() => import('./pages/ProductPage').then(m => ({ default: m.ProductPage })));
const MissionPage = lazy(() => import('./pages/MissionPage').then(m => ({ default: m.MissionPage })));
const PricingPage = lazy(() =>
  import('./pages/PricingPage').then(m => ({ default: m.PricingPage }))
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then(m => ({ default: m.ContactPage }))
);
const SessionSuccessPage = lazy(() =>
  import('./pages/SessionSuccessPage').then(m => ({ default: m.SessionSuccessPage }))
);
const PasswordResetPage = lazy(() =>
  import('./pages/PasswordResetPage').then(m => ({ default: m.PasswordResetPage }))
);
const LoginRedirectPage = lazy(() =>
  import('./pages/LoginRedirectPage').then(m => ({ default: m.LoginRedirectPage }))
);
const GetStartedPage = lazy(() =>
  import('./pages/GetStartedPage').then(m => ({ default: m.GetStartedPage }))
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage }))
);
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const SupportPage = lazy(() => import('./pages/SupportPage').then(m => ({ default: m.SupportPage })));
const SubprocessorsPage = lazy(() => import('./pages/SubprocessorsPage').then(m => ({ default: m.SubprocessorsPage })));

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

function AppContent({ lenisRef }: { lenisRef: RefObject<Lenis | null> }) {
  const location = useLocation();
  const isFullScreenPage = location.pathname === '/session-success' || location.pathname === '/reset-password' || location.pathname === '/login' || location.pathname === '/get-started';

  useEffect(() => {
    updateRouteMetadata(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [location.pathname, lenisRef]);

  if (isFullScreenPage) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/session-success" element={<SessionSuccessPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/login" element={<LoginRedirectPage />} />
          <Route path="/get-started" element={<GetStartedPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark flex flex-col">
      <Navigation />

      <main className="flex-grow">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/mission" element={<MissionPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/subprocessors" element={<SubprocessorsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}

function App() {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const lenis = new Lenis({
      duration: 0.85,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: !prefersReducedMotion,
      anchors: !prefersReducedMotion,
      // Nested product surfaces, such as the Electron Home demo, own their
      // wheel interaction and must not be redirected to the marketing page.
      prevent: (node) => node.closest('[data-interactive-home-app]') !== null,
    });
    lenisRef.current = lenis;

    const handleIframeWheel = (event: Event): void => {
      const deltaY = (event as CustomEvent<number>).detail;
      if (typeof deltaY === 'number') {
        lenis.scrollTo(lenis.targetScroll + deltaY, { programmatic: false });
      }
    };
    window.addEventListener('revont:iframe-wheel', handleIframeWheel);

    let animationFrameId = 0;
    function raf(time: number) {
      lenis.raf(time);
      animationFrameId = requestAnimationFrame(raf);
    }
    animationFrameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('revont:iframe-wheel', handleIframeWheel);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppContent lenisRef={lenisRef} />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
